import { forModel } from "@eusoft/webapp-jsx";
import {  Action, Content, contentInfo, formatCurrency, formatDate, formatText,  formatTextSimple,  MaterialIcon,  Message,  objectEditor,  Popup,  required,  requiredTrue,  validEmail, type ViewNode } from "@eusoft/webapp-ui";
import { navigatePage } from "../../actions/Common";
import { Card } from "../../components/Card";
import { Field } from "../../components/Field";
import { StatusTag } from "../../components/StatusTag";
import { TransactionList } from "../../components/TransactionList";
import { SortDirection, type Guid, type IPaymentCardInfo, type ITransactionView, type IWalletFullInfo } from "../../entities/Commands";
import { formatCardStatus, formatMarkDown } from "../../helpers/Format";
import { ApiError, executeAsync } from "../../services/DataService";
import { userSession } from "../../services/UserSession";
import "./CustomerWalletPage.scss";
import { popupEditAsync } from "@eusoft/webapp-framework/helpers/Editor";
import { registerComponent } from "@eusoft/webapp-core/Component";
import { Logo } from "../../components/Logo";

export interface ICustomerWalletArgs {
    walletId?: Guid;
}

interface IRegisterData {
    pin: number;    
    name: string;   
    email: string;
    phone: string;  
    acceptPrivacy: boolean;
}

export class CustomerWalletPage extends Content<ICustomerWalletArgs>  {

    constructor() {

        super();

        this.init(CustomerWalletPage, {
            title: "wallet",
            style: ["panel"],            
            body: forModel(this, m => <>
                <div on-click={()=> m.openSite()} className="brand">
                    <Logo />
                    <span>PayApp</span>
                </div>
                <div className="balance">{formatCurrency(m.wallet?.balance)}</div>
                {m.cardInfo && <Card style="fieldset">
                    <Field label="card-number-full">{m.cardInfo.cardCode}</Field>
                    <Field label="owner">{m.cardInfo.customer?.name}</Field>
                    <Field label="email">{m.cardInfo.customer?.email}</Field>
                    <Field label="phone-number">{m.cardInfo.customer?.mobileNumber}</Field>
                    <Field label="blocked-on">{formatDate(m.cardInfo.blockTime, formatTextSimple("format-date-time") as string)}</Field>
                    <Field label="card-state">
                        <StatusTag {...formatCardStatus(m.cardInfo.cardStatus)} />
                    </Field>
                </Card>}
                {m.transactions && <TransactionList transactions={m.transactions} />}
            </>)            
        });
    }

    openSite() {
        window.open("https://www.pay-app.it", "_blank");
    }

    async registerWalletAsync() {

        const editor = objectEditor<IRegisterData>(bld => <>
            {bld.text(a => a.name, {
                validators: [required],
                editor: {
                }
            })}
            {bld.text(a => a.email, {
                validators: [required, validEmail],
                editor: {
                    type: "email",  
                }
            })}
            {bld.text(a => a.phone, {
                label: "phone-number",
                validators: [required],
                editor: {
                    type: "tel",
                }
            })}
            {bld.number(a => a.pin, {
                validators: [required],
                editor: {
                }
            })}
            {bld.boolean(a => a.acceptPrivacy, {
                validators: [requiredTrue],
                label: formatMarkDown("accept-privacy"),
                editor: {
                }
            })}
        </>, {
            style: "vertical",
            commitMode: "manual-inplace",
            inputField: {
                style: "filled"
            },
        });

        const data = {
            email: this.wallet.owner?.email,
            name: this.wallet.owner?.name,
            phone: this.wallet.owner?.mobileNumber,
        } as IRegisterData;

        const res = await popupEditAsync(editor, data, {
            okLabel: "register",
            title: "register-wallet",
            cancelLabel: "not-now",
            message: <Message type="warning">
                {formatText("msg-wallet-not-protected")}
            </Message>
        });

        if (!res)
            return;
        
        const protect = await executeAsync("ProtectWallet", {
            ownerEmail: data.email, 
            ownerName: data.name,   
            ownerPhoneNumber: data.phone,   
            walletId: this.walletId,    
            ownerPinCode: data.pin.toString()  
        });

        if (!protect)
            return false;

        const session = await userSession.openAsync({
            walletId: this.walletId,    
            password: data.pin.toString()
        }, true);

        return session?.accessToken != null;
    }

    async requestResetPin() {
        const result = await executeAsync("RequestResetWalletPin", {
            walletId: this.walletId,
        });
        
        if(!result) {
            return;
        }
        this.successMessage = formatText("msg-reset-pin-requested");
    }
    
    async loginWalletAsync() {

        const data = {
            pin: undefined as number
        }

        const editor = objectEditor(bld => <>
            {bld.number(a => a.pin, {
                label: "pin",
                validators: [required],
                editor: {
                }
            })}
        </>, {
            value: data,
            style: "vertical",
            commitMode: "manual-inplace",
            inputField: {
                style: "filled"
            },
        });

        const popup = new Popup({
            body: forModel(this, m => <>
                {editor}
                {m.errorMessage && <Message type="error" style="in-popup">
                    {m.errorMessage}
                </Message>}
                {m.successMessage && <Message type="info" >
                    {m.successMessage}
                </Message>}
                <Action
                  name="reset-pin"
                  onExecuteAsync={() => this.requestResetPin()}
                  style="text"
                >
                    reset-pin
                </Action>
            </>),
            name: "login-wallet",
            title:  formatText("login-wallet"),
            actions: [{
                name: "cancel",
                text: "cancel",
            },{
                name: "ok",
                text: "login",
                executeAsync: async () => {

                    if (!await editor.commitAsync())
                        return;

                    this.errorMessage = ""; 

                    const session = await userSession.openAsync({
                        walletId: this.walletId,
                        password: data.pin.toString()
                    }, true);

                    if (!session?.accessToken) {
                        this.errorMessage = formatText("err-invalid-pin");
                        return false;
                    }
                    
                    return true;
                }
            }]
        });

        const res = await popup.showAsync();

        return res == "ok";
    }

    protected async loadWorkAsync(walletId: Guid) {

        if (!walletId)
            return false;

        this.walletId = walletId;

        let needPin = false;

        if (userSession.active?.walletId != walletId)
            userSession.invalidate();

        this.wallet = await executeAsync("GetWalletInfo", {
            walletId: this.walletId,
        }, {

            onError: (err) => {
                if (err instanceof ApiError && err.has("err-not-authorized")) {
                    needPin = true;
                    return true;
                }
                return false;
            }
        });

        if (needPin) {

            if (!await this.loginWalletAsync())
                return false;

            this.wallet = await executeAsync("GetWalletInfo", {
                walletId: this.walletId,
            });
        }

        if (!this.wallet)
            return false;

        if (!this.wallet.isSecured)
            await this.registerWalletAsync();

        this.cardsInfo = await Promise.all(this.wallet.cardCodes.map(a => executeAsync("GetCardInfo", { cardCode: a })));

        this.transactions = (await executeAsync("ListTransactions", {
            fromWalletId: this.walletId,
            toWalletId: this.walletId,
            sort: {
                direction: SortDirection.Descending,
                property: "Time"
            },
            pagination: {
                limit: 10,
                offset: 0
            }
        }))?.items;

        return true;
    }


    override async onLoadAsync(args: ICustomerWalletArgs) {

        if (!await this.loadWorkAsync(args.walletId)) {

            navigatePage("error", {
                message: "err-not-authorized"
            }, "replace");

            return false;
        }

        return true;
    }

    get cardInfo() {
        return this.cardsInfo[0];
    }

    cardsInfo: IPaymentCardInfo[];

    walletId: Guid;

    wallet?: IWalletFullInfo;

    transactions: ITransactionView[];

    errorMessage?: ViewNode;  
    
    successMessage?: ViewNode;  

    hideContext = true;

    static override info = contentInfo({
        name: "customer-wallet",
        route: "/wallet/{walletId}",
        icon: <MaterialIcon style="blue" name="paid" />,
        factory: () => new CustomerWalletPage(),
        //features: [Authorize(bld => bld.allowTemporary())]
    });
}

registerComponent(CustomerWalletPage, "CustomerWalletPage");